import { BLOG_API_PREFIX } from '../config/site';
import { Blog } from '../types/blog';

type BlogRecord = Record<string, unknown>;

export class BlogApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'BlogApiError';
  }
}

function asRecord(value: unknown): BlogRecord {
  return value && typeof value === 'object' ? (value as BlogRecord) : {};
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const node = document.createElement('div');
    node.innerHTML = html;
    return node.textContent || '';
  }
  return html.replace(/<[^>]+>/g, ' ');
}

export function getReadingTime(value: Pick<Blog, 'htmlContent' | 'readingTime'>): string {
  if (value.readingTime) return value.readingTime;

  const words = stripHtml(value.htmlContent).trim().split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
}

function getCreatorName(value: unknown): string {
  if (typeof value === 'string') return value.trim();

  const creator = asRecord(value);
  const firstName = asString(creator.firstName) || asString(creator.first_name);
  const lastName = asString(creator.lastName) || asString(creator.last_name);
  const combinedName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    asString(creator.name) ||
    asString(creator.fullName) ||
    asString(creator.displayName) ||
    combinedName ||
    asString(creator.username) ||
    asString(creator.email) ||
    'SaveItPro Team'
  );
}

function getCategoryName(value: unknown): string {
  if (typeof value === 'string') return value.trim();

  const category = asRecord(value);
  const nestedCategoryName = category.categoryId ? getCategoryName(category.categoryId) : '';

  return (
    nestedCategoryName ||
    asString(category.name) ||
    asString(category.title) ||
    asString(category.label) ||
    asString(category.slug)
  );
}

function normalizeBlog(raw: unknown): Blog {
  const record = asRecord(raw);
  const title = asString(record.title) || 'Untitled blog';
  const htmlContent =
    asString(record.htmlContent) || asString(record.content) || asString(record.body);
  const readingTime =
    asString(record.readingTime) ||
    asString(record.readTime) ||
    asString(record.readingTimeText) ||
    asString(record.estimatedReadingTime);
  const normalized = {
    id: asString(record.id) || asString(record._id) || slugify(title),
    slug: asString(record.slug) || asString(record.id) || asString(record._id) || slugify(title),
    title,
    htmlContent,
    readingTime,
    creator:
      asString(record.creatorName) ||
      asString(record.authorName) ||
      asString(record.createdByName) ||
      getCreatorName(record.creator || record.author || record.createdBy || record.user),
    category:
      asString(record.categoryName) ||
      asString(record.categoryTitle) ||
      asString(record.categoryLabel) ||
      getCategoryName(
        record.topicId || record.category || record.blogCategory || record.topic || record.tag,
      ) ||
      'Uncategorized',
    thumbnailUrl:
      asString(record.thumbnailUrl) ||
      asString(record.thumbnail) ||
      asString(record.imageUrl) ||
      asString(record.coverImage),
    createdAt: asString(record.createdAt) || asString(record.publishedAt),
  };

  return {
    ...normalized,
    readingTime: getReadingTime(normalized),
  };
}

function getCollection(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;

  const record = asRecord(payload);
  const candidates = [record.blogs, record.posts, record.items, record.data, record.results];
  const collection = candidates.find(Array.isArray);
  return collection || [];
}

function getSingle(payload: unknown): unknown {
  const record = asRecord(payload);
  return record.blog || record.post || record.data || payload;
}

async function parseJsonResponse<T>(
  response: Response,
  parser: (payload: unknown) => T,
): Promise<T> {
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new BlogApiError(body?.message || 'Unable to load blogs right now.', response.status);
  }

  const payload = await response.json();
  return parser(payload);
}

export async function fetchBlogs(): Promise<Blog[]> {
  const response = await fetch(BLOG_API_PREFIX + '/list');
  return parseJsonResponse(response, (payload) => getCollection(payload).map(normalizeBlog));
}

export async function fetchBlogBySlug(slug: string): Promise<Blog> {
  const response = await fetch(`${BLOG_API_PREFIX}/${encodeURIComponent(slug)}`);
  return parseJsonResponse(response, (payload) => normalizeBlog(getSingle(payload)));
}
